import React from "react";

// Mirror the backend payload shape from
// agent_runtime/surfaces/superdoc.py:emit_suggested_actions.
export interface Suggestion {
  category: string;
  label: string;
  program_slug: string;
  prefill_args: Record<string, unknown>;
  confidence: number;
  rationale: string;
}

export interface SuggestedActionsData {
  suggestions: Suggestion[];
  event_id: string;
  generated_at: string;
  confidence_floor: number;
}

interface SuggestedActionsProps {
  data: SuggestedActionsData;
  // Clicking a chip injects the suggestion's label into the chat input as
  // a user message (no auto-send — the clinician reviews before sending).
  // Phase G will route through the downstream program_slug instead.
  onSendFollowup: (label: string) => void;
  disabled?: boolean;
}

// Human-readable headers for the 6 fixed categories from the backend
// prompt. Keep this map in sync with
// agent_runtime/programs/prediction.py:PREDICTION_CATEGORIES.
const CATEGORY_LABELS: Record<string, string> = {
  based_on_recent: "Based on Recent",
  documentations: "Documentations",
  clinical_decision: "Clinical Decision",
  medications: "Medications",
  patient_communications: "Patient Communications",
  coding_and_referral: "Coding and Referral",
};

const SparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
    <path d="M7 0L8.2 4.8L13 6L8.2 7.2L7 12L5.8 7.2L1 6L5.8 4.8L7 0Z" fill="#6366F1" />
  </svg>
);

const SuggestedActions: React.FC<SuggestedActionsProps> = ({ data, onSendFollowup, disabled }) => {
  const suggestions = data?.suggestions || [];
  if (suggestions.length === 0) return null;

  // Group by category, preserving the order categories first appear in
  // the suggestions list so the highest-priority category renders first.
  const grouped = new Map<string, Suggestion[]>();
  for (const s of suggestions) {
    const key = s.category || "based_on_recent";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(s);
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <SparkleIcon />
        <span style={styles.headerText}>Suggested next actions</span>
      </div>
      {Array.from(grouped.entries()).map(([category, items]) => (
        <div key={category} style={styles.categoryBlock}>
          <div style={styles.categoryLabel}>{CATEGORY_LABELS[category] || category}</div>
          <div style={styles.chipsRow}>
            {items.map((s, idx) => (
              <button
                key={`${category}-${idx}`}
                type="button"
                title={s.rationale || undefined}
                style={{
                  ...styles.chip,
                  ...(disabled ? styles.chipDisabled : {}),
                }}
                disabled={disabled}
                onClick={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  onSendFollowup(s.label);
                }}
                onMouseEnter={(e) => {
                  if (!disabled) e.currentTarget.style.backgroundColor = "#F3F4F6";
                }}
                onMouseLeave={(e) => {
                  if (!disabled) e.currentTarget.style.backgroundColor = "#FFFFFF";
                }}
              >
                <SparkleIcon />
                <span style={styles.chipText}>{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginTop: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
  },
  categoryBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  categoryLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  chipsRow: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  chip: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    transition: "background-color 0.15s",
    fontFamily: "inherit",
  },
  chipText: {
    fontSize: "13px",
    color: "#374151",
    lineHeight: "1.4",
  },
  chipDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
};

export default SuggestedActions;
